import React, { useState, useEffect, useRef } from 'react';
import axios from '../../utils/axiosConfig';
import { toast } from '../../hooks/use-toast';
import { FileText, Eye, Edit, Trash2, Plus, Tag, Folder, X } from 'lucide-react';
import UltimateBlogEditor from './UltimateBlogEditor';
import { BlogStatsCards, AddCategoryModal } from './BlogManagerParts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]); // Store all posts
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0 });
  const [activeTab, setActiveTab] = useState('posts'); // posts, categories
  const [selectedCategory, setSelectedCategory] = useState('all'); // Category filter
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState([]); // For bulk delete
  
  // Category Management
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  
  // Track component mount status to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  useEffect(() => {
    fetchData();
    
    // Cleanup: mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = async () => {
    try {
      if (!isMountedRef.current) return; // Don't fetch if unmounted
      
      setLoading(true);
      
      // Use admin endpoint to get ALL posts (including drafts)
      const [postsRes, catsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/blog/posts?limit=100`),
        axios.get(`${BACKEND_URL}/api/blog/categories`)
      ]);
      
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      
      const fetchedPosts = postsRes.data.posts || [];
      
      setAllPosts(fetchedPosts); // Store all posts
      setPosts(fetchedPosts); // Initially show all
      setCategories(catsRes.data || []);
      
      setStats({
        total: fetchedPosts.length,
        published: fetchedPosts.filter(p => p.status === 'published').length,
        draft: fetchedPosts.filter(p => p.status === 'draft').length
      });
    } catch (error) {
      console.error('Error fetching blog data:', error);
      // Only show toast if component is still mounted
      if (isMountedRef.current) {
        toast({
          title: "Error",
          description: "Failed to fetch blog data",
          variant: "destructive"
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Filter posts by category (using slug because IDs don't match)
  const filterPostsByCategory = (categorySlug, categoryName) => {
    setSelectedCategory(categoryName);
    setSelectedPosts([]); // Clear selection when changing category
    if (categoryName === 'all') {
      setPosts(allPosts);
    } else {
      const filtered = allPosts.filter(post => 
        post.category === categorySlug
      );
      setPosts(filtered);
    }
  };

  const openEditModal = (post) => {
    setEditingPost({...post});
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPost(null);
  };

  const savePost = async (postData) => {
    if (!postData) return;
    
    // Validate required fields
    if (!postData.title || !postData.content_html) {
      toast({
        title: "Validation Error",
        description: "Title and content are required",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setSaving(true);
      
      // Store current category filter to restore after save
      const currentCategoryFilter = selectedCategory;
      
      // Check if creating new post or updating existing
      const isNewPost = !editingPost.id;
      const url = isNewPost 
        ? `${BACKEND_URL}/api/admin/blog/posts`
        : `${BACKEND_URL}/api/admin/blog/posts/${editingPost.id}`;
      const method = isNewPost ? 'post' : 'put';
      
      // Build payload with proper null handling for optional fields
      // Handle author field - convert object to string if needed
      let authorValue = 'Admin';
      if (postData.author) {
        if (typeof postData.author === 'string') {
          authorValue = postData.author;
        } else if (typeof postData.author === 'object' && postData.author.name) {
          authorValue = postData.author.name;
        }
      }
      
      const payload = {
        title: postData.title,
        slug: postData.slug || postData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        content_html: postData.content_html,
        excerpt: postData.excerpt || '',
        category: postData.category || 'other-sport-branches',
        featured_image: postData.featured_image || null,
        status: postData.status || 'draft',
        featured: postData.featured || false,
        tags: postData.tags || [],
        meta_title: postData.meta_title || null,
        meta_description: postData.meta_description || null,
        canonical_url: postData.canonical_url || null,
        robots_index: postData.robots_index !== false,
        robots_follow: postData.robots_follow !== false,
        allow_comments: postData.allow_comments !== false,
        allow_pingbacks: postData.allow_pingbacks !== false,
        author: authorValue
      };
      
      console.log('[DEBUG] Sending payload to backend:', JSON.stringify(payload, null, 2));
      
      const response = await axios[method](url, payload);
      
      console.log('Save response:', response.data);
      
      // Refresh data first before closing modal to prevent white screen
      await fetchData();
      
      // Restore the same category filter after refresh
      if (currentCategoryFilter !== 'all') {
        const category = categories.find(c => c.name === currentCategoryFilter);
        if (category) {
          filterPostsByCategory(category.slug, category.name);
        }
      }
      
      // Close modal and show success message after data is loaded
      closeEditModal();
      
      toast({
        title: "Success",
        description: isNewPost ? "Post created successfully" : "Post updated successfully",
      });
    } catch (error) {
      console.error('Error saving post:', error);
      console.error('Error details:', error.response?.data);
      
      // Get detailed error message
      let errorMessage = "Failed to save post";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
      
      // Don't close modal on error - let user fix the issue
      // Modal stays open so user can see and fix validation errors
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/blog/posts/${postId}`);
      toast({
        title: "Success",
        description: "Post deleted successfully",
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive"
      });
    }
  };

  // Bulk delete posts
  const bulkDeletePosts = async () => {
    if (selectedPosts.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedPosts.length} post(s)?`)) return;
    
    try {
      // Delete all selected posts
      await Promise.all(
        selectedPosts.map(postId => 
          axios.delete(`${BACKEND_URL}/api/admin/blog/posts/${postId}`)
        )
      );
      
      toast({
        title: "Success",
        description: `${selectedPosts.length} post(s) deleted successfully`,
      });
      
      setSelectedPosts([]); // Clear selection
      fetchData();
    } catch (error) {
      console.error('Error deleting posts:', error);
      toast({
        title: "Error",
        description: "Failed to delete some posts",
        variant: "destructive"
      });
    }
  };

  // Toggle post selection
  const togglePostSelection = (postId) => {
    setSelectedPosts(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  // Toggle select all posts
  const toggleSelectAll = () => {
    if (selectedPosts.length === posts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(posts.map(post => post.id));
    }
  };

  const addCategory = async () => {
    if (!newCategory.name.trim()) {
      toast({
        title: "Error",
        description: "Category name is required",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Generate slug from category name - handle Turkish characters
      let slug = newCategory.name.toLowerCase()
        .trim()
        // Replace Turkish characters with ASCII equivalents
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        // Remove special chars but keep letters, numbers, spaces and hyphens
        .replace(/[^a-z0-9\s-]/g, '')
        // Replace spaces with hyphens
        .replace(/\s+/g, '-')
        // Remove duplicate hyphens
        .replace(/-+/g, '-')
        // Remove leading/trailing hyphens
        .replace(/^-+|-+$/g, '');
      
      // Validate slug is not empty
      if (!slug || slug.length < 2) {
        toast({
          title: "Error",
          description: "Category name must contain at least 2 valid characters",
          variant: "destructive"
        });
        return;
      }
      
      const payload = {
        name: newCategory.name.trim(),
        slug: slug,
        description: newCategory.description?.trim() || null,
        icon: null,
        order: 0,
        visible: true
      };
      
      console.log('[DEBUG] Sending category payload:', payload);
      const response = await axios.post(`${BACKEND_URL}/api/admin/blog/categories`, payload);
      console.log('[DEBUG] Category creation SUCCESS:', response.data);
      
      // Directly add the new category to the state (optimistic update)
      const newCat = response.data;
      setCategories(prevCategories => [...prevCategories, newCat]);
      
      // Reset form and close modal
      setNewCategory({ name: '', description: '' });
      setShowAddCategoryModal(false);
      
      // Show success message
      toast({
        title: "Success",
        description: "Category added successfully",
      });
      
    } catch (error) {
      console.error('[ERROR] Error adding category:', error);
      console.error('[ERROR] Error response:', error.response?.data);
      
      // Build user-friendly error message
      let errorMessage = "Failed to add category";
      
      try {
        const errorDetail = error.response?.data?.detail;
        
        if (errorDetail) {
          if (Array.isArray(errorDetail)) {
            errorMessage = errorDetail.map(err => {
              const location = err.loc ? err.loc.join('.') : 'field';
              const message = err.msg || 'Invalid value';
              return `${location}: ${message}`;
            }).join(', ');
          } else if (typeof errorDetail === 'string') {
            errorMessage = errorDetail;
          } else {
            errorMessage = "Invalid data format";
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
      } catch (parseError) {
        console.error('[ERROR] Failed to parse error:', parseError);
        errorMessage = "An unexpected error occurred";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/blog/categories/${categoryId}`);
      toast({
        title: "Success",
        description: "Category deleted successfully",
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat ? cat.name : slug;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Blog Management</h2>
        <p className="text-gray-600 dark:text-gray-400">Manage blog posts and categories</p>
      </div>

      {/* Stats Cards */}
      <BlogStatsCards stats={stats} categoriesCount={categories.length} />

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'posts'
                ? 'border-b-2 border-purple-600 text-red-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Posts ({stats.total})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'categories'
                ? 'border-b-2 border-purple-600 text-red-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
            }`}
          >
            Categories ({categories.length})
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'posts' ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          {/* Category Filter */}
          <div className="p-4 border-b bg-gradient-to-r from-red-50 to-gray-50 dark:from-gray-800 dark:to-gray-800 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Folder className="w-5 h-5 text-red-600 dark:text-red-400" />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Filter by Category:</h3>
              </div>
              <button
                onClick={() => {
                  setEditingPost({
                    title: '',
                    slug: '',
                    content_html: '',
                    excerpt: '',
                    category: selectedCategory !== 'all' ? categories.find(c => c.name === selectedCategory)?.slug : '',
                    featured_image: '',
                    status: 'draft',
                    featured: false,
                    tags: [],
                    post_format: 'standard',
                    meta_title: '',
                    meta_description: '',
                    meta_keywords: '',
                    canonical_url: '',
                    robots_index: true,
                    robots_follow: true,
                    visibility: 'public',
                    password: '',
                    scheduled_date: '',
                    scheduled_time: '',
                    allow_comments: true,
                    allow_pingbacks: true,
                    author: 'Admin',
                    custom_fields: []
                  });
                  setShowEditModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Add New Post</span>
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => filterPostsByCategory('all', 'all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-gradient-to-r from-red-600 to-black text-white shadow-md'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                }`}
              >
                📚 All Posts ({allPosts.length})
              </button>
              {categories.map((cat) => {
                const categoryPostCount = allPosts.filter(p => 
                  p.category === cat.slug
                ).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => filterPostsByCategory(cat.slug, cat.name)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === cat.name
                        ? 'bg-gradient-to-r from-red-600 to-black text-white shadow-md'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {cat.icon || '📁'} {cat.name} ({categoryPostCount})
                  </button>
                );
              })}
            </div>
            {selectedCategory !== 'all' && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-red-200">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  📊 Showing <span className="font-bold text-red-600">{posts.length}</span> post(s) in category: 
                  <span className="font-bold text-red-600 ml-1">{selectedCategory}</span>
                </p>
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading posts...</div>
          ) : posts.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {selectedCategory === 'all' 
                ? 'No posts found' 
                : `No posts found in "${selectedCategory}" category`}
            </div>
          ) : (
            <>
              {/* Bulk Actions Bar */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPosts.length === posts.length && posts.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-red-600 rounded border-gray-300 dark:border-gray-600 focus:ring-red-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select All ({selectedPosts.length} selected)
                    </span>
                  </label>
                </div>
                
                {selectedPosts.length > 0 && (
                  <button
                    onClick={bulkDeletePosts}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected ({selectedPosts.length})
                  </button>
                )}
              </div>

              <div className="divide-y">
                {posts.map((post) => (
                  <div key={post.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      {/* Checkbox */}
                      <div className="flex items-start pt-1">
                        <input
                          type="checkbox"
                          checked={selectedPosts.includes(post.id)}
                          onChange={() => togglePostSelection(post.id)}
                          className="w-4 h-4 text-red-600 rounded border-gray-300 dark:border-gray-600 focus:ring-red-500 cursor-pointer"
                        />
                      </div>

                      {/* Post Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{post.title}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            post.status === 'published' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          }`}>
                            {post.status}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{post.excerpt}</p>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <span>
                            <Tag className="w-4 h-4 inline mr-1" />
                            {getCategoryName(post.category)}
                          </span>
                          <span>
                            <Eye className="w-4 h-4 inline mr-1" />
                            {post.views || 0} views
                          </span>
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        <a
                          href={`/blog/post/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Post"
                        >
                          <Eye className="w-5 h-5" />
                        </a>
                        <button
                          onClick={() => openEditModal(post)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Edit Post"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => deletePost(post.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          {/* Add Category Button */}
          <div className="mb-6 flex justify-end">
            <button
              data-testid="blog-add-category-button"
              onClick={() => setShowAddCategoryModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Add New Category
            </button>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div key={category.id} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">{category.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
                  </div>
                  <button
                    onClick={() => deleteCategory(category.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <FileText className="w-4 h-4" />
                  <span>{category.post_count || 0} posts</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Advanced Blog Editor */}
      {showEditModal && editingPost && (
        <UltimateBlogEditor
          post={editingPost}
          categories={categories}
          onClose={closeEditModal}
          onSave={savePost}
        />
      )}

      {/* Add Category Modal */}
      <AddCategoryModal
        show={showAddCategoryModal}
        newCategory={newCategory}
        setNewCategory={setNewCategory}
        onClose={() => {
          setShowAddCategoryModal(false);
          setNewCategory({ name: '', description: '' });
        }}
        onAdd={addCategory}
      />
    </div>
  );
};

export default BlogManager;
